import { Store, Tag, History, TrendingUp, DollarSign } from 'lucide-react';

export function MarketplaceStats({ listings, ownedTokens, myListings, transactions }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <div className="glass-card p-4">
        <p className="text-gray-400 text-sm">Active Listings</p>
        <p className="text-2xl font-bold">{listings.length}</p>
      </div>
      <div className="glass-card p-4">
        <p className="text-gray-400 text-sm">Your Tokens</p>
        <p className="text-2xl font-bold">{ownedTokens.reduce((sum, t) => sum + t.tokens_owned, 0)}%</p>
      </div>
      <div className="glass-card p-4">
        <p className="text-gray-400 text-sm">Your Listings</p>
        <p className="text-2xl font-bold">{myListings.filter(l => l.status === 'active').length}</p>
      </div>
      <div className="glass-card p-4">
        <p className="text-gray-400 text-sm">Transactions</p>
        <p className="text-2xl font-bold">{transactions.length}</p>
      </div>
    </div>
  );
}

export function MarketplaceTabs({ activeTab, setActiveTab }) {
  const tabs = [
    { id: 'browse', label: 'Browse' },
    { id: 'my-listings', label: 'My Listings' },
    { id: 'history', label: 'History' }
  ];
  
  return (
    <div className="flex gap-2 mb-6">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === tab.id 
              ? 'bg-purple-500 text-white' 
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
          data-testid={`tab-${tab.id}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function ListingCard({ listing, onBuy }) {
  return (
    <div className="glass-card p-5" data-testid={`listing-${listing.id}`}>
      <div className="flex items-start gap-4 mb-4">
        <img 
          src={listing.track?.cover_url} 
          alt={listing.track?.title}
          className="w-16 h-16 rounded-lg object-cover"
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate">{listing.track?.title}</h3>
          <p className="text-sm text-gray-400">{listing.track?.artist}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white/5 rounded-lg p-3">
          <p className="text-xs text-gray-400">Amount</p>
          <p className="font-semibold">{listing.tokens_amount}%</p>
        </div>
        <div className="bg-white/5 rounded-lg p-3">
          <p className="text-xs text-gray-400">Price/Token</p>
          <p className="font-semibold text-green-400">${listing.price_per_token}</p>
        </div>
      </div>
      
      <div className="flex items-center justify-between">
        <p className="text-lg font-bold">
          Total: ${(listing.tokens_amount * listing.price_per_token).toFixed(2)}
        </p>
        <button 
          onClick={() => onBuy(listing)}
          className="btn-primary text-sm py-2"
          data-testid={`buy-${listing.id}`}
        >
          Buy Now
        </button>
      </div>
    </div>
  );
}

export function MyListingCard({ listing, onCancel }) {
  return (
    <div className="glass-card p-5 flex items-center justify-between" data-testid={`my-listing-${listing.id}`}>
      <div className="flex items-center gap-4">
        <img 
          src={listing.track?.cover_url} 
          alt={listing.track?.title}
          className="w-14 h-14 rounded-lg object-cover"
        />
        <div>
          <h3 className="font-semibold">{listing.track?.title}</h3>
          <p className="text-sm text-gray-400">
            {listing.tokens_amount}% @ ${listing.price_per_token}/token
          </p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          listing.status === 'active' 
            ? 'bg-green-500/20 text-green-400' 
            : listing.status === 'sold'
            ? 'bg-purple-500/20 text-purple-400'
            : 'bg-gray-500/20 text-gray-400'
        }`}>
          {listing.status}
        </span>
        {listing.status === 'active' && (
          <button 
            onClick={() => onCancel(listing.id)}
            className="text-red-400 hover:text-red-300 text-sm"
            data-testid={`cancel-${listing.id}`}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

export function TransactionCard({ tx }) {
  return (
    <div className="glass-card p-5 flex items-center justify-between" data-testid={`tx-${tx.id}`}>
      <div className="flex items-center gap-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          tx.type === 'bought' ? 'bg-green-500/20' : 'bg-purple-500/20'
        }`}>
          {tx.type === 'bought' 
            ? <TrendingUp className="w-5 h-5 text-green-400" /> 
            : <DollarSign className="w-5 h-5 text-purple-400" />
          }
        </div>
        <div>
          <h3 className="font-semibold">{tx.track?.title}</h3>
          <p className="text-sm text-gray-400">
            {tx.type === 'bought' ? 'Bought' : 'Sold'} {tx.tokens_amount}%
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={`font-semibold ${tx.type === 'bought' ? 'text-red-400' : 'text-green-400'}`}>
          {tx.type === 'bought' ? '-' : '+'}${tx.total_price.toFixed(2)}
        </p>
        <p className="text-xs text-gray-400">
          {new Date(tx.transaction_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

export function EmptyState({ type }) {
  const configs = {
    browse: {
      icon: Store,
      title: 'No listings yet',
      description: 'Be the first to list your tokens for sale!'
    },
    'my-listings': {
      icon: Tag,
      title: 'No listings',
      description: "You haven't listed any tokens for sale"
    },
    history: {
      icon: History,
      title: 'No transactions',
      description: 'Your marketplace transactions will appear here'
    }
  };
  
  const config = configs[type] || configs.browse;
  const Icon = config.icon;
  
  return (
    <div className={`empty-state glass-card py-16 ${type === 'browse' ? 'col-span-full' : ''}`}>
      <Icon className="empty-state-icon w-16 h-16" />
      <h3 className="text-xl font-semibold mb-2">{config.title}</h3>
      <p className="text-gray-400">{config.description}</p>
    </div>
  );
}
